import { Link, useLocation } from 'react-router-dom'
import { type ReactNode, useState } from 'react'
import { Brain, BookOpen, Puzzle, Wrench, Play, Activity, BarChart3, Trophy, Star, Menu, X, Globe, Upload, User, Database, ShieldCheck, Settings, Layers } from 'lucide-react'
import { useLang } from '../i18n/LangContext'
import type { TranslationKey } from '../i18n/translations'

const navItems: { path: string; labelKey: TranslationKey; icon: typeof Brain }[] = [
  { path: '/', labelKey: 'nav.home', icon: Brain },
  { path: '/competencies', labelKey: 'nav.competency', icon: BookOpen },
  { path: '/tasks', labelKey: 'nav.tasks', icon: Puzzle },
  { path: '/tools', labelKey: 'nav.tools', icon: Wrench },
  { path: '/rubrics', labelKey: 'nav.rubric', icon: Star },
  { path: '/run', labelKey: 'nav.run', icon: Play },
  { path: '/traces', labelKey: 'nav.traces', icon: Activity },
  { path: '/reports', labelKey: 'nav.reports', icon: BarChart3 },
  { path: '/leaderboard', labelKey: 'nav.leaderboard', icon: Trophy },
  { path: '/tasks/contribute', labelKey: 'nav.contribute', icon: Upload },
  { path: '/expert', labelKey: 'nav.expert', icon: User },
  { path: '/pipeline', labelKey: 'nav.pipeline', icon: Database },
  { path: '/governance', labelKey: 'nav.governance', icon: ShieldCheck },
  { path: '/batch', labelKey: 'nav.batch', icon: Layers },
  { path: '/settings', labelKey: 'nav.settings', icon: Settings },
]

export default function Layout({ children }: { children: ReactNode }) {
  const location = useLocation()
  const { lang, toggleLang, t } = useLang()
  const [mobileOpen, setMobileOpen] = useState(false)

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
      <header className="sticky top-0 z-50 border-b" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-secondary)' }}>
        <div className="container-page px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 shrink-0">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'var(--color-accent-glow)' }}>
                <Brain size={20} style={{ color: 'var(--color-accent)' }} />
              </div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-lg" style={{ color: 'var(--color-text-primary)' }}>DecisionCraft</span>
                <span className="text-xs px-2 py-0.5 rounded font-medium" style={{ color: 'var(--color-accent)', backgroundColor: 'var(--color-accent-glow)' }}>MVP</span>
              </div>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-0.5">
              {navItems.map(item => {
                const isActive = location.pathname === item.path ||
                  (item.path !== '/' && location.pathname.startsWith(item.path))
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all"
                    style={{
                      color: isActive ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                      backgroundColor: isActive ? 'var(--color-accent-glow)' : 'transparent',
                    }}
                    onMouseEnter={e => {
                      if (!isActive) {
                        e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)'
                        e.currentTarget.style.color = 'var(--color-text-primary)'
                      }
                    }}
                    onMouseLeave={e => {
                      if (!isActive) {
                        e.currentTarget.style.backgroundColor = 'transparent'
                        e.currentTarget.style.color = 'var(--color-text-secondary)'
                      }
                    }}
                  >
                    <item.icon size={16} />
                    <span>{t(item.labelKey)}</span>
                  </Link>
                )
              })}
            </nav>

            {/* Right: Language Toggle + Mobile Menu Button */}
            <div className="flex items-center gap-2">
              <button
                onClick={toggleLang}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                style={{
                  color: 'var(--color-text-secondary)',
                  backgroundColor: 'var(--color-bg-tertiary)',
                  border: '1px solid var(--color-border)',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-accent)'; e.currentTarget.style.color = 'var(--color-accent)' }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-border)'; e.currentTarget.style.color = 'var(--color-text-secondary)' }}
                title={lang === 'zh' ? 'Switch to English' : '切换为中文'}
              >
                <Globe size={14} />
                <span>{lang === 'zh' ? '中文' : 'EN'}</span>
              </button>

              {/* Mobile menu button */}
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="lg:hidden p-2 rounded-lg transition-colors"
                style={{ color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-bg-tertiary)' }}
              >
                {mobileOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>

          {/* Mobile Nav */}
          {mobileOpen && (
            <nav className="lg:hidden pb-4 flex flex-col gap-1">
              {navItems.map(item => {
                const isActive = location.pathname === item.path ||
                  (item.path !== '/' && location.pathname.startsWith(item.path))
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors"
                    style={{
                      color: isActive ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                      backgroundColor: isActive ? 'var(--color-accent-glow)' : 'var(--color-bg-tertiary)',
                    }}
                  >
                    <item.icon size={16} />
                    <span>{t(item.labelKey)}</span>
                  </Link>
                )
              })}
            </nav>
          )}
        </div>
      </header>

      <main className="flex-1 w-full">
        {children}
      </main>

      <footer className="border-t py-6" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-secondary)' }}>
        <div className="container-page px-4 sm:px-6 lg:px-8 xl:px-12 text-center">
          <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
            {t('footer.tagline')}
          </p>
          <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)', opacity: 0.7 }}>
            {t('footer.copyright')}
          </p>
        </div>
      </footer>
    </div>
  )
}
